import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listStockLedger, createStockAdjustment, listItems, StockLedgerEntry, InventoryItem } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const MOVEMENT_TYPES = [
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
  { value: 'waste', label: 'Waste' },
  { value: 'internal_use', label: 'Internal Use' },
];

const StockLedger: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get('item_id') ? parseInt(searchParams.get('item_id')!) : undefined;

  const [entries, setEntries] = useState<StockLedgerEntry[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    inventory_item_id: itemId ? String(itemId) : '',
    movement_type: 'manual_adjustment',
    quantity_delta: '',
    unit: '',
    unit_cost: '',
    notes: '',
  });

  const load = useCallback(() => {
    Promise.all([
      listStockLedger(sessionToken, itemId, 50, 0),
      listItems(sessionToken),
    ])
      .then(([ledgerRes, itemsRes]) => {
        setEntries(ledgerRes.entries);
        setTotal(ledgerRes.total);
        setItems(itemsRes.items);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, itemId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await createStockAdjustment(sessionToken, {
        inventory_item_id: Number(form.inventory_item_id),
        movement_type: form.movement_type,
        quantity_delta: Number(form.quantity_delta),
        unit: form.unit,
        unit_cost: form.unit_cost ? Number(form.unit_cost) : undefined,
        notes: form.notes || undefined,
      });
      setShowForm(false);
      setForm({ inventory_item_id: '', movement_type: 'manual_adjustment', quantity_delta: '', unit: '', unit_cost: '', notes: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;
  const canWrite = ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <h1>📋 Stock Ledger</h1>
        <div className="inv2-header-actions">
          {canWrite && <button onClick={() => setShowForm(true)} className="inv2-btn inv2-btn-primary">+ New Adjustment</button>}
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}
      <p className="inv2-count">{total} total entries{itemId ? ` for this item` : ''}</p>

      {showForm && (
        <div className="inv2-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="inv2-modal" onClick={e => e.stopPropagation()}>
            <h2>New Stock Adjustment</h2>
            <form onSubmit={handleSubmit} className="inv2-form">
              <div className="inv2-form-group">
                <label>Item *</label>
                <select value={form.inventory_item_id} onChange={e => setForm(p => ({ ...p, inventory_item_id: e.target.value }))} required className="inv2-select">
                  <option value="">— Select item —</option>
                  {items.map(i => <option key={i.id} value={i.id}>{i.name} ({i.on_hand_quantity} {i.base_unit})</option>)}
                </select>
              </div>
              <div className="inv2-form-group">
                <label>Movement Type *</label>
                <select value={form.movement_type} onChange={e => setForm(p => ({ ...p, movement_type: e.target.value }))} className="inv2-select">
                  {MOVEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="inv2-form-group">
                <label>Quantity (+ to add, - to reduce) *</label>
                <input type="number" step="any" value={form.quantity_delta} onChange={e => setForm(p => ({ ...p, quantity_delta: e.target.value }))} required className="inv2-input" />
              </div>
              <div className="inv2-form-group">
                <label>Unit *</label>
                <input type="text" value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} required placeholder="gram, ml, count..." className="inv2-input" />
              </div>
              <div className="inv2-form-group">
                <label>Unit Cost ($)</label>
                <input type="number" step="0.0001" value={form.unit_cost} onChange={e => setForm(p => ({ ...p, unit_cost: e.target.value }))} className="inv2-input" />
              </div>
              <div className="inv2-form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="inv2-textarea" />
              </div>
              <div className="inv2-form-actions">
                <button type="submit" className="inv2-btn inv2-btn-primary">Save</button>
                <button type="button" onClick={() => setShowForm(false)} className="inv2-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="inv2-table-container">
        <DataTable
          rows={entries}
          rowKey={(entry) => entry.id}
          caption="Stock movements"
          moreLabel="Cost & reference"
          empty="No entries"
          columns={[
            { key: 'item', header: 'Item', priority: 'primary', render: (entry) => <Link to={`/inventory/items/${entry.inventory_item_id}`} className="inv2-link">{entry.item_name || `#${entry.inventory_item_id}`}</Link> },
            {
              key: 'qty', header: 'Quantity', priority: 'summary', numeric: true,
              render: (entry) => (
                <span className={Number(entry.quantity_delta) > 0 ? 'inv2-text-success' : 'inv2-text-danger'}>
                  {Number(entry.quantity_delta) > 0 ? '+' : ''}{Number(entry.quantity_delta).toFixed(1)} {entry.unit}
                </span>
              ),
            },
            { key: 'type', header: 'Type', priority: 'summary', render: (entry) => <span className="inv2-movement-type">{entry.movement_type.replace(/_/g, ' ')}</span> },
            { key: 'date', header: 'Date', priority: 'summary', render: (entry) => new Date(entry.created_at).toLocaleString() },
            { key: 'by', header: 'By', priority: 'summary', render: (entry) => entry.created_by_name || '—' },
            { key: 'ucost', header: 'Unit Cost', priority: 'detail', numeric: true, render: (entry) => (entry.unit_cost != null ? `$${Number(entry.unit_cost).toFixed(4)}` : '—') },
            { key: 'tcost', header: 'Total Cost', priority: 'detail', numeric: true, render: (entry) => (entry.total_cost != null ? `$${Number(entry.total_cost).toFixed(2)}` : '—') },
            { key: 'ref', header: 'Reference', priority: 'detail', render: (entry) => entry.reference_type || '—' },
            { key: 'notes', header: 'Notes', priority: 'detail', render: (entry) => entry.notes || '—' },
          ]}
        />
      </div>
    </div>
  );
};

export default StockLedger;
